/**
 * Daily Progress Report — auto-generated, read-only.
 *
 * Replaces the old EPC DPR (a manual multi-department form: execution/
 * planning/qa_qc/safety/stores/procurement sections, WBS/BOQ line items,
 * per-project). RMC's daily operational reality is already fully captured
 * by Production, Dispatch, and Lab Master — this just rolls it up for a
 * chosen date. Nothing to fill in, nothing to submit.
 * Backend: GET /api/dpr/daily-summary?date=YYYY-MM-DD
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Boxes, Truck, FlaskConical, Users, CheckCircle2, XCircle, Clock, ArrowUpRight,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet } from '../lib/api';
import { inrCompact } from '../lib/format';

const Tile = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="flex items-start justify-between mb-2">
      <div className="kpi-overline">{label}</div>
      <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}>
        {Icon && <Icon size={13} />}
      </div>
    </div>
    <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

const Section = ({ title, action, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm">
    <div className="p-5 border-b border-slate-200 flex items-center justify-between">
      <h3 className="font-display font-bold text-base">{title}</h3>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

export default function DPR() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiGet('/dpr/daily-summary', { date })
      .then(setData)
      .catch(() => toast.error('Failed to load daily summary'))
      .finally(() => setLoading(false));
  }, [date]);

  return (
    <div data-testid="dpr-page">
      <PageHeader
        overline="Operations"
        title="Daily Progress"
        subtitle="Auto-generated from Production, Dispatch, and Lab Master — nothing to fill in."
      />

      <div className="flex items-center gap-2 mb-5">
        <input
          type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="input" style={{ maxWidth: 200 }}
          data-testid="dpr-date-picker"
        />
        <button
          onClick={() => setDate(new Date().toISOString().slice(0, 10))}
          className="px-3 py-2 text-xs font-display font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-slate-50"
        >
          Today
        </button>
      </div>

      {loading && <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Tile label="Production" value={`${data.production.total_qty_m3} m³`}
              sub={`${data.production.batch_count} batch(es)${data.production.shortage_count ? ` · ${data.production.shortage_count} short` : ''}`}
              icon={Boxes} accent={data.production.shortage_count ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-[#1F7A6E]'} />
            <Tile label="Dispatch" value={data.dispatch.trip_count}
              sub={`${data.dispatch.total_qty_m3} m³ · ${inrCompact(data.dispatch.total_revenue)}`}
              icon={Truck} accent="bg-emerald-50 text-emerald-700" />
            <Tile label="Cube Tests" value={data.lab.cube_tests_completed}
              sub={`${data.lab.cube_tests_passed} pass · ${data.lab.cube_tests_failed} fail · ${data.lab.cube_tests_cast} cast`}
              icon={FlaskConical} accent={data.lab.cube_tests_failed ? 'bg-red-50 text-red-700' : 'bg-teal-50 text-[#1F7A6E]'} />
            <Tile label="Attendance" value={`${data.attendance.attendance_pct}%`}
              sub={`${data.attendance.present} / ${data.attendance.total_active} present`}
              icon={Users} accent="bg-slate-100 text-slate-700" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Production — by grade" action={
              <a href="/app/production" onClick={(e) => { e.preventDefault(); navigate('/app/production'); }}
                className="text-xs font-display font-bold uppercase tracking-widest text-[#1F7A6E] hover:underline flex items-center gap-1">
                View <ArrowUpRight size={12} />
              </a>
            }>
              {!data.production.by_grade.length ? (
                <div className="text-sm text-slate-400 py-4 text-center">No production logged for this date.</div>
              ) : (
                <div className="space-y-2">
                  {data.production.by_grade.map((g) => (
                    <div key={g.grade} className="flex items-center justify-between text-sm">
                      <span className="font-display font-semibold">{g.grade}</span>
                      <span className="tabular-nums text-slate-700">{g.qty_m3} m³</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Dispatch & Lab" action={
              <a href="/app/dispatch" onClick={(e) => { e.preventDefault(); navigate('/app/dispatch'); }}
                className="text-xs font-display font-bold uppercase tracking-widest text-[#1F7A6E] hover:underline flex items-center gap-1">
                View <ArrowUpRight size={12} />
              </a>
            }>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Vehicles dispatched</span>
                  <span className="font-display font-bold tabular-nums">{data.dispatch.vehicles_dispatched}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-slate-600"><Clock size={13} /> Cube tests cast</span>
                  <span className="font-display font-bold tabular-nums">{data.lab.cube_tests_cast}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 size={13} /> Passed</span>
                  <span className="font-display font-bold tabular-nums text-emerald-700">{data.lab.cube_tests_passed}</span>
                </div>
                {data.lab.cube_tests_failed > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-red-700"><XCircle size={13} /> Failed</span>
                    <span className="font-display font-bold tabular-nums text-red-700">{data.lab.cube_tests_failed}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Material tests logged</span>
                  <span className="font-display font-bold tabular-nums">{data.lab.material_tests_logged}</span>
                </div>
              </div>
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
